import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';

// Transform lang to contentful locale
export function normalizeLocale(locale) {
  if (!locale) {
    return locale;
  }

  return locale.replace(
    /(\w{2})-(\w{2})/,
    (match, group1, group2) => `${group1}-${group2.toUpperCase()}`
  );
}

// This function adds the locale next to the sys.id value
// This exists because the graphql result cannot currently return the locale
// just like the regular SDK queries do, and we need the locale next to the entry for link resolvers to work correctly
// so we have this function we can use in the graphql queries to return the entry with the locale
// data can be entry, collection, or list (array) and we'll try to handle all of these internally for usage simplicity
// If contentful ever allows returning the locale as part of the graphql result, we may remove this entirely
export function appendLocale(data, locale) {
  const normalizedLocale = normalizeLocale(locale);

  // Data is single entry
  if (data?.sys?.id !== undefined) {
    return {
      ...data,
      sys: {
        ...data.sys,
        locale: normalizedLocale,
      },
    };
  }

  // Data is collection
  if (Array.isArray(data?.items)) {
    return { ...data, items: data.items.map((entry) => appendLocale(entry, locale)) };
  }

  // Data is collection list
  if (Array.isArray(data)) {
    return data.map((entry) => appendLocale(entry, locale));
  }

  return data;
}

export function isContentfulEntry(entry) {
  if (!entry) {
    return null;
  }
  return Object.prototype.hasOwnProperty.call(entry, 'sys');
}

export function entryContainsField(entry, field) {
  if (!entry) {
    return null;
  }
  return Object.prototype.hasOwnProperty.call(entry.fields, field);
}

export function getEntryContentType(entry) {
  return entry.__typename || entry.sys.contentType.sys.id;
}

export function entryNeedsResolving(entry) {
  return entry?.sys?.type === 'Link';
}

export function linksToMap(links) {
  const assetsMap = new Map();
  const entriesMap = new Map();

  Array.from(links?.assets?.block || []).reduce((map, entry) => {
    map.set(entry.sys.id, entry);
    return map;
  }, assetsMap);

  Array.from(links?.entries?.block || []).reduce((map, entry) => {
    map.set(entry.sys.id, entry);
    return map;
  }, entriesMap);

  Array.from(links?.entries?.inline || []).reduce((map, entry) => {
    map.set(entry.sys.id, entry);
    return map;
  }, entriesMap);

  return { entries: entriesMap, assets: assetsMap };
}

export function resolveLocalEntry(entry, linksMap) {
  const unresolvedLinkType = entry.sys.linkType;
  switch (unresolvedLinkType) {
    case 'Entry':
      return linksMap.entries.get(entry.sys.id);
    case 'Asset':
      return linksMap.assets.get(entry.sys.id);
    default:
      throw new Error(`No way to resolve link for type ${unresolvedLinkType}`);
  }
}

export function assetExists(entry) {
  return !!entry?.url || entry?.fields?.file?.url !== undefined;
}

export function contentfulFieldHasContent(field) {
  if (!field) {
    return false;
  }
  if (field?.nodeType) {
    return documentToPlainTextString(field).length > 0;
  }
  return !!field;
}

export function getEditLink(entry) {
  const {
    sys: {
      space: {
        sys: { id: spaceId },
      },
      id,
    },
  } = entry;
  return `https://app.contentful.com/spaces/${spaceId}/entries/${id}`;
}

export function getPublishedStaticPathsFromAllLocalesItems(itemsWithAllLocales) {
  return itemsWithAllLocales.reduce((paths, { fields }) => {
    const { slug: slugWithinLocale, published: publishedWithinLocale } = fields;
    return paths.concat(
      Object.entries(slugWithinLocale)
        .filter(([locale]) => publishedWithinLocale[locale])
        .map(([locale, slug]) => ({
          params: { slug },
          locale: locale.toLocaleLowerCase(),
        }))
    );
  }, []);
}

export function getStaticPathsFromAllLocalesItems(itemsWithAllLocales) {
  return itemsWithAllLocales.reduce((paths, { fields }) => {
    const { slug: slugWithinLocale } = fields;
    return paths.concat(
      Object.entries(slugWithinLocale).map(([locale, slug]) => ({
        params: { slug },
        locale: locale.toLocaleLowerCase(),
      }))
    );
  }, []);
}
